import React from 'react';
import CombatTooltip from '../GameTip/CombatTooltip';
import { STATUS_TAG_ICONS, renderStatusText } from './statusWordGlyphs';
import {
  PROMOTED_TAG_META,
  isPromotedTag,
  promotedChromeVars,
} from './promotedEnemyStatusVocabulary';

/* Inline TRUE status-tag renderer for prose (the pre-combat announce line).

   Unlike renderStatusText (which renders a flat glyph+word for tooltip COPY and
   must stay tooltip-free to avoid nesting a CombatTooltip inside a CombatTooltip),
   this renders the ACTUAL HUD tag element: the real `bls-status-header--tab
   bls-status-header--<slug>` pill (same CSS classes → identical color + chrome as
   the in-combat tag) wrapped in the real <CombatTooltip> so it has the same
   hover-card as the HUD tag, with a concise "what this status does" line.

   The host that renders this MUST sit inside a `.combat-status-rail` ancestor
   (the announce wraps it in `.combat-status-rail--inline`) because the per-slug
   color rules in CombatPlayerbar.css are scoped to `.batch-player-bar` /
   `.combat-status-rail`. */

// keyword (lowercased) → { statusClass, iconKey, name, color, line }. statusClass
// is the real `bls-status-header--<statusClass>` modifier; name/color/line mirror
// the matching Player*Tag's CombatTooltip so the hover reads identically.
const TAG_META = {
  // Athero pool statuses. `lipid` gained its `def` on 2026-08-15, the last of the
  // scaling statuses to get one: it predates the all-statuses-as-chips convention
  // below (2026-07-12) and was never revisited, so every "Inflicts [lipid]" row in
  // the game rendered with an EMPTY right column while the Pestil, Anaphyl and HTN
  // rows carried theirs. Worse, decode.jsx:253 drops a tag's authored magnitude on
  // the stated grounds that the def is the single source of the number — so with no
  // def, chylo's and veldra's loads stated no number anywhere.
  //
  // The def is this status' own `line`, compressed into the def register: second
  // person dropped, lowercased, terminal period dropped. That is the exact
  // relationship `resistance pressure` (below) has between its line and its def,
  // and it is what keeps the swap lossless — `statusTooltipLines` lets a def
  // REPLACE the line on the chip hover and the compendium's status card, so a def
  // stating a different fact would delete this one. Engine warrant for "scaling":
  // veldra `triglyceride_lash` (2 × stacks + 1), idol `stacks_times_two`,
  // lipo-alpha `stacks_times_plaques` (mini_boss_engine/registry.py:65, :94, :155).
  lipid: { statusClass: 'lipid', iconKey: 'lipid', name: 'Lipid Stacks', color: 'rgb(251, 191, 36)', line: 'Lipid-scaling enemies hit harder for every stack on you.', def: [{ t: 'lipid-scaling enemies hit harder for every stack' }] },
  // `plaque` carries the MAGNITUDE in its def, and that is safe for a reason worth
  // stating: ELDRA IS THE ONLY SOURCE OF PLAQUE IN THE ENGINE. `applies_status_effect:
  // "plaques"` with `plaque_delta: 1` and `max_hp_delta: -5` occurs exactly once
  // repo-wide (mini_boss_engine/registry.py:121-124); nothing else writes the pool.
  // So -5 is not "eldra's number" as opposed to the status', it IS the status'
  // number, and putting it here is what finally renders it: eldra's own authored
  // `hp('−5 max')` sits AFTER the chip on its row and the single-chip descriptor
  // discards everything after the chip, so the one place the frontend states the
  // permanent cost had never reached a screen.
  //
  // ⚠️ An earlier revision of this comment claimed both plaque mentions were CONVERT
  // rows and that a def could never surface. That was wrong: eldra's second row
  // (announceDecode.js) carries a single tag and takes the single-chip branch, which
  // is exactly the branch that draws a def. Athero's row IS a convert row and
  // correctly shows none (decode.jsx:338-341).
  //
  // Shaped after `end-organ` below, the same mechanic on the HTN side (permanent
  // unhealable max-HP loss), plus the magnitude end-organ has no single value for.
  plaque: { statusClass: 'plaque', iconKey: 'plaque', name: 'Atherosclerotic Plaque', color: 'rgb(244, 63, 94)', line: 'Permanent plaque. The scarred HP cannot be healed, so your healing ceiling drops.', def: [{ g: 'hp', v: '−5 max', chip: true }, { t: 'permanent' }] },
  plaques: { statusClass: 'plaque', iconKey: 'plaque', name: 'Atherosclerotic Plaque', color: 'rgb(244, 63, 94)', line: 'Permanent plaque. The scarred HP cannot be healed, so your healing ceiling drops.', def: [{ g: 'hp', v: '−5 max', chip: true }, { t: 'permanent' }] },
  histamine: { statusClass: 'histamine', iconKey: 'histamine', name: 'Histamine Stacks', color: 'rgb(244, 114, 182)', line: "The enemy's next strike scales with your histamine stacks.", def: [{ g: 'damage', v: '+2', owner: 'enemy' }] },
  deposit: { statusClass: 'deposit', iconKey: 'deposit', name: 'Immune-Complex Deposits', color: 'rgb(167, 139, 250)', line: 'Each deposit cuts your strike damage (down to a 10% floor).', def: [{ g: 'down' }, { g: 'damage', v: '15%', owner: 'player' }] },
  deposits: { statusClass: 'deposit', iconKey: 'deposit', name: 'Immune-Complex Deposits', color: 'rgb(167, 139, 250)', line: 'Each deposit cuts your strike damage (down to a 10% floor).', def: [{ g: 'down' }, { g: 'damage', v: '15%', owner: 'player' }] },
  delayed: { statusClass: 'delayed', iconKey: 'delayed', name: 'Delayed Damage', color: 'rgb(45, 212, 191)', line: 'Queued hits land in a few rounds, and carry to the next enemy.', def: [{ g: 'damage', owner: 'enemy' }, { g: 'rounds', v: '2' }, { t: 'carries over' }] },
  // Pestil pool statuses (all-statuses-as-chips). def = a plain-English EFFECT line
  // (the enemy's attack pattern, cause -> consequence) surfaced under the "Inflicts
  // [chip]" row and in the bare status hover — Edward 2026-07-12: the enemy tooltip
  // should describe what the attack DOES, minimally, not a cryptic glyph fragment.
  'resistance pressure': { statusClass: 'pressure', iconKey: 'pressure', name: 'Resistance Pressure', color: 'rgb(245, 158, 11)', line: 'Wrong answers build pressure that makes later enemies harder to crack.', def: [{ t: 'wrong answers make later enemies hit harder' }] },
  cdiff: { statusClass: 'cdiff', iconKey: 'cdiff', name: 'C. diff', pillLabel: 'C. diff', pillItalic: true, color: 'rgb(132, 204, 22)', line: 'Even correct strikes build C. diff; at the threshold it bursts and raises incoming damage.', def: [{ t: 'even right strikes build it; bursts at 5 for 8, then +2 incoming' }] },
  reservoir: { statusClass: 'reservoir', iconKey: 'reservoir', name: 'Latent Reservoir', color: 'rgb(168, 85, 247)', line: 'Fires for damage every few rounds, and survives the enemy that seeded it.', def: [{ t: 'fires +3 every 3 rounds, and outlives the enemy' }] },
  // Bradyx / Convul / Algo pool statuses. 🔴 ALL SEVEN GAINED A `def` ON 2026-08-15,
  // for exactly the reason `lipid` did above: they predate the 2026-07-12
  // all-statuses-as-chips convention and were never revisited, so every
  // "Inflicts [chip]" row naming one of them painted an EMPTY right column while
  // the Pestil, Anaphyl and HTN rows carried theirs. Measured on the compendium
  // before the fix: the bradyx, convul and algo folders had ZERO populated right
  // columns between them, against nine on Ten Shin's.
  //
  // 🔴 EACH DEF IS THAT STATUS' OWN `line` COMPRESSED, NOT A NEW FACT. A def
  // REPLACES the line on every surface (`statusTooltipLines` below, and the
  // compendium's MECHANICS page reads the same either/or at mechanics.jsx:868-870),
  // so a def stating something else DELETES a sentence from three surfaces to gain
  // a right column. Register: second person dropped, lowercased, terminal period
  // dropped, one clause. Same relationship `resistance pressure` and `lipid` have.
  //
  // ⚠️ `levodopa` deliberately gets NO def. It is a PLAYER-side resource
  // (`levodopa_strike_count` is written only on the player's own strike,
  // batch_combat_engine.py:5132), and the block below states player resources carry
  // a `line` only and never an Inflicts `def` — the same rule that keeps `streak`
  // bare. Its empty right column on dyskinex's row is correct, not unfinished.
  pulse: { statusClass: 'pulse', iconKey: 'pulse', name: 'Pulsatile Dose', color: 'rgb(74, 222, 128)', line: 'Levodopa runs ON/OFF. Miss the OFF window and you take a hit.', def: [{ t: 'levodopa runs 3-on / 1-off; missed OFF coverage deals 4' }] },
  freeze: { statusClass: 'freeze', iconKey: 'freeze', name: 'Freeze', color: 'rgb(56, 189, 248)', line: 'Freeze-of-gait skips your turn; enough freezes and you lose.', def: [{ t: 'freeze-of-gait skips a turn; 3 freezes lose the fight' }] },
  levodopa: { statusClass: 'dose', iconKey: 'dose', name: 'Levodopa Load', color: 'rgb(148, 163, 184)', line: 'Re-dosing covers the pulse but loads dyskinesia over time.' },
  // ⚠️ No accuracy NUMBER here on purpose. `accuracy_modifier` is written at
  // batch_combat_engine.py:5251 (−0.05) and :4568, and has NO consumer anywhere in
  // backend/services or backend/api, so whether it is 5 points or 5% is unknowable
  // from the engine. The cap (6) is read and is stated.
  confusion: { statusClass: 'confusion', iconKey: 'confusion', name: 'Confusion', color: 'rgb(192, 132, 252)', line: 'Anticholinergic confusion shaves your accuracy; at the cap you lose.', def: [{ t: 'anticholinergic strikes shave accuracy; 6 stacks lose the fight' }] },
  seizure: { statusClass: 'seizure', iconKey: 'seizure', name: 'Seizure Threshold', color: 'rgb(168, 85, 247)', line: 'The boss is invulnerable while the seizure threshold holds; AED strikes lower it.', def: [{ t: 'boss invulnerable above 0; AED strikes drop it −2; Stata spawns at 5' }] },
  // The magnitude is safe here under the same one-source test the `plaque` block
  // above applies: `required_strike_sequence` occurs exactly ONCE repo-wide
  // (registry.py:626, stata's own spec, read at batch_combat_engine.py:4757), so
  // this IS the status' number rather than one enemy's.
  protocol: { statusClass: 'sequence', iconKey: 'sequence', name: 'Status Protocol', color: 'rgb(244, 114, 182)', line: 'Strike the right drugs in the right order, in time, or take the full hit.', def: [{ t: 'wrong order, off-class or 2-round expiry costs 12; 3 misses lose' }] },
  toxicity: { statusClass: 'toxica', iconKey: 'toxica', name: 'Toxicity', color: 'rgb(251, 146, 60)', line: 'Dose above the therapeutic window and you self-inflict toxicity.', def: [{ t: 'dosing above the window self-inflicts 5 damage and −5 max HP' }] },
  tolerance: { statusClass: 'tolerance', iconKey: 'tolerance', name: 'Opioid Tolerance', color: 'rgb(245, 158, 11)', line: 'Every opioid strike builds tolerance, blunting your damage.', def: [{ t: 'each opioid strike dulls opioid damage; adjuvants reduce it' }] },
  // HTN wardens — the enemy-inflicted BP/end-organ statuses (all-statuses-as-chips, 2026-07-12).
  // `def` is the single source of truth surfaced in the enemy "Inflicts [chip]" row.
  // `pressure` keeps its OWN key/class (pressure-stack, red) — NOT conflated with
  // Pestil's amber "resistance pressure" — and is prose-guarded so a metaphorical
  // "pressure" in un-decoded enemy copy never upgrades to the HTN pill.
  // Pressure does NOTHING on its own (verified in engine: batch_combat_engine.py —
  // inert ammunition, no tick/decay/damage until a pressure-scaling warden reads it),
  // so its self-def (the "Inflicts [Pressure] .... Silent?" right column, and the chip's
  // own hover — same `def`) is a deliberately teasing italic "Silent?" (Edward 2026-07-13).
  // `line` kept as the underlying truth (dormant unless `def` is removed).
  pressure: { statusClass: 'pressure-stack', iconKey: 'pressureStack', name: 'Pressure', color: 'rgb(248, 113, 113)', line: 'Pressure-scaling enemies hit harder for every stack of BP load on you.', def: [{ t: 'Silent?', italic: true }], prose: false },
  // The enemy "Inflicts [Fibrosis]" pill matches the player's Fibrotic Remodeling
  // chip in short disease form (Edward 2026-07-14). Same `tag('end-organ')` token
  // also carries Sympra's `pressure × [Fibrosis]` cascade-pool multiplier — kept
  // consistent so chip and every enemy reference read the same disease.
  'end-organ': { statusClass: 'end-organ', iconKey: 'endOrgan', name: 'Fibrotic Remodeling', pillLabel: 'Fibrosis', color: 'rgb(159, 18, 57)', line: 'Permanent organ scarring. The scarred HP cannot be healed, so your healing ceiling drops.', def: [{ t: 'permanent' }] },
  // Player-side augment resources + HTN meters. Registered so any tooltip mention
  // renders the real chip (Edward: "propagate the tag style to all tags"). These
  // are never enemy-inflicted, so they carry `line` only (no Inflicts def).
  cytokine: { statusClass: 'cytokine', iconKey: 'cytokine', name: 'Cytokine Stack', color: 'rgb(96, 165, 250)', line: 'The enemy can spend these for a burst of 2 + 2 per stack.' },
  immune: { statusClass: 'immune', iconKey: 'immune', name: 'Immune Charge', color: 'rgb(96, 165, 250)', line: 'The enemy can burn the whole charge at once as direct damage.', prose: false },
  glycogen: { statusClass: 'glycogen', iconKey: 'glycogen', name: 'Glycogen Reserve', color: 'rgb(45, 212, 160)', line: 'Banks overheal. Auto-spent to survive an otherwise-lethal hit.' },
  ketone: { statusClass: 'ketone', iconKey: 'ketone', name: 'Ketone Charges', color: 'rgb(167, 139, 250)', line: 'Builds when you take damage. At cap: burst-heal 30 HP, then reset.' },
  ketones: { statusClass: 'ketone', iconKey: 'ketone', name: 'Ketone Charges', color: 'rgb(167, 139, 250)', line: 'Builds when you take damage. At cap: burst-heal 30 HP, then reset.' },
  diuresis: { statusClass: 'diuresis', iconKey: 'diuresis', name: 'Diuresis', color: 'rgb(56, 189, 248)', line: 'Drains every enemy each round by a flat amount set by the equipped diuretics.' },
  potassium: { statusClass: 'potassium', iconKey: 'potassium', name: 'Potassium', color: 'rgb(96, 165, 250)', line: 'Serum potassium is fixed by the equipped regimen for this fight; danger rises at either pole.' },
  lactic: { statusClass: 'lactic', iconKey: 'lactic', name: 'Lactic Acidosis', color: 'rgb(252, 165, 165)', line: 'Your damage out drops and damage in rises with each stack.' },
  streak: { statusClass: 'streak', iconKey: 'streak', name: 'Streak', color: 'rgb(251, 146, 60)', line: 'With Hyperfocus equipped, each hit adds bonus strike damage.', prose: false },
  'htn-pulse': { statusClass: 'htn-pulse', iconKey: 'htnPulse', name: 'Pulse Shift', color: 'rgb(248, 113, 113)', line: 'Your antihypertensive loadout shifts your heart rate, changing how many questions each round throws.' },
  // Blue, the app's established "the incoming thing was negated" colour (the
  // ADR-dodge absorb chip), NOT pressure's red: this is the answer to pressure,
  // not a dose of it. The def states the magnitude-independence, which is the one
  // thing about this mechanic a player is most likely to guess wrong.
  vasodilation: { statusClass: 'vasodilation', iconKey: 'vasodilation', name: 'Vasodilation', color: 'rgb(96, 165, 250)', line: 'Widened vessels absorb an enemy pressure application before it lands.', def: [{ t: 'one charge voids one whole pressure application' }] },
  // Production is authoritative for statuses referenced by the shared family
  // cards. Spread last so stale Jon-era lipid/plaque copy cannot win.
  ...PROMOTED_TAG_META,
};

// Longest-first so multi-word "resistance pressure" wins over a bare "pressure".
// `prose: false` statuses (immune / streak / pressure — common English words) are
// registered chips but excluded from the free-prose auto-upgrade so a metaphorical
// mention never hijacks into a colored pill; they still render via explicit tag()
// tokens and StatusTagPill.
const TAGS_RE = new RegExp(
  `\\b(${Object.keys(TAG_META).filter((k) => TAG_META[k].prose !== false).sort((a, b) => b.length - a.length).join('|')})\\b`,
  'gi',
);

export { TAG_META };

// Status hover content has one selection rule everywhere: a structured `def`
// wins whenever present, and the legacy prose `line` is only a fallback for
// statuses without a def. Keep this centralized so no render surface can drift.
export function statusTooltipLines(meta) {
  if (!meta) return [];
  return meta.def ? [{ stats: meta.def }] : (meta.line ? [meta.line] : []);
}

/* The compact right column inside an enemy tooltip is flavor, not a second
   mechanics authority. Current production statuses may author a short aside;
   Jon-only metadata safely falls back to its structured definition until it is
   migrated. Keeping the selection here means every enemy card uses the same
   convention without copying it into the Compendium. */
export function statusAsideLines(meta) {
  if (!meta) return [];
  return meta.aside || meta.def || [];
}

/* The TRUE HUD tag pill (+ its hover card) for one status word — the exact
   `bls-status-header--<slug>` element the combat HUD renders, so a mention in
   announce copy / decode lines reads identically to the live tag. Extracted so
   GameTip/decode.jsx can compose it into authored decode lines.

   `interactive={false}` returns just the pill chrome with NO CombatTooltip
   wrapper — the STATIC form the enemy hover tooltip embeds in an "Inflicts
   [chip]" row (Edward 2026-07-12). A tooltip must never nest another tooltip,
   so inside the enemy hover the pill is decorative chrome and its def is shown
   inline beside it instead of behind a second hover. */
export function StatusTagPill({ word, meta, interactive = true, className = '' }) {
  // `meta.icon` and `meta.generic` serve a status from decode.jsx's optional extra
  // vocabulary, which has no STATUS_TAG_ICONS key and no per-slug CSS. Registry
  // entries set neither, so they resolve exactly as before.
  const icon = meta.icon || STATUS_TAG_ICONS[meta.iconKey];
  const lines = statusTooltipLines(meta);
  // The pill shows the tag, never a magnitude. A count OF the tag is expressed as an
  // `N ×` prefix in a `.gt-formula` beside it (see GameTip/decode.jsx), not stuffed
  // inside the chip — the in-pill number was a one-enemy invention, removed
  // 2026-07-26.
  const generic = meta.generic || isPromotedTag(word) || isPromotedTag(meta.statusClass);
  const pill = (
    <span
      style={generic ? promotedChromeVars(meta) : undefined}
      data-status-tag-word={word}
      className={[
      'bls-status-header',
      'bls-status-header--tab',
      `bls-status-header--${meta.statusClass}`,
      generic && 'bls-status-header--generic',
      'status-tag-inline',
      className,
    ].filter(Boolean).join(' ')}>
      {icon && icon(14, 'bls-status-header-icon')}
      <span className="bls-status-tab-value">
        {meta.pillLabel
          ? <span style={{ textTransform: 'none', ...(meta.pillItalic ? { fontStyle: 'italic' } : null) }}>{meta.pillLabel}</span>
          : word}
      </span>
    </span>
  );
  if (!interactive) return pill;
  return (
    <CombatTooltip
      as="span"
      color={meta.color}
      name={meta.name}
      placement="above-left"
      icon={icon ? icon(18, 'combat-tt__node-icon') : null}
      sections={[{ lines }]}
      bare
    >
      {pill}
    </CombatTooltip>
  );
}

/* Render a copy string with inline TRUE status tags. Each matched status word
   becomes the real HUD tag pill + its CombatTooltip hover. Non-matching text
   runs through the status-word glyph pass (tooltip canon 2026-06-11) so core
   mechanic words (damage / HP / heal / rounds…) get their inline glyph anchors
   here too — previously they rendered plain on the announce surface. */
export function renderStatusTags(text) {
  if (!text) return null;
  const parts = String(text).split(TAGS_RE);
  return (
    <>
      {parts.map((part, i) => {
        const meta = TAG_META[part.toLowerCase()];
        if (!meta) return <React.Fragment key={i}>{renderStatusText(part)}</React.Fragment>;
        return <StatusTagPill key={i} word={part} meta={meta} />;
      })}
    </>
  );
}
